import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Context-driven sidebar slot mirroring the legacy
 * `useSetAppSidebarSlot` from web/src/components/app/app-sidebar-slot.tsx.
 *
 * Why this layer exists: the /codes/$id detail page needs to push its
 * own identity card + lifecycle actions + mode toggle into the global
 * dashboard sidebar (under the nav menu), not into the page's main
 * content. Without this slot the layout has to fight column-widths to
 * reproduce the legacy "Lifecycle in sidebar" pattern from commits
 * eab1d60 / 806fb01 / df97a37.
 *
 * Contract:
 *   - <AppSidebarSlotProvider> wraps the AppShell so every route inside
 *     it can mount content.
 *   - `useSetAppSidebarSlot(node)` registers a render node for the
 *     lifetime of the calling component. Returning to a route without a
 *     slot clears it automatically via the effect cleanup.
 *   - `useAppSidebarSlot()` reads the current slot — only the AppShell
 *     itself consumes it.
 */
type SlotContext = {
  node: ReactNode | null;
  setNode: (next: ReactNode | null) => void;
};

const AppSidebarSlotContext = createContext<SlotContext | null>(null);

export function AppSidebarSlotProvider({ children }: { children: ReactNode }) {
  const [node, setNode] = useState<ReactNode | null>(null);
  const stableSetNode = useCallback((next: ReactNode | null) => {
    setNode(next);
  }, []);
  const value = useMemo<SlotContext>(
    () => ({ node, setNode: stableSetNode }),
    [node, stableSetNode],
  );
  return (
    <AppSidebarSlotContext.Provider value={value}>
      {children}
    </AppSidebarSlotContext.Provider>
  );
}

export function useAppSidebarSlot(): ReactNode | null {
  const ctx = useContext(AppSidebarSlotContext);
  return ctx?.node ?? null;
}

/**
 * Mounts `node` into the dashboard sidebar for the lifetime of the
 * caller. Clears the slot on unmount so navigating away from a
 * detail page reverts the sidebar to nav-only.
 */
export function useSetAppSidebarSlot(node: ReactNode | null): void {
  const ctx = useContext(AppSidebarSlotContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setNode(node);
    return () => ctx.setNode(null);
  }, [ctx, node]);
}
